import { CheckRounded } from '@mui/icons-material';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';

import useAccountSwitcher, { getSwitchableAccountName } from 'hooks/useAccountSwitcher';
import { useProfilePicture } from 'hooks/useMyAccount';
import useTranslate from 'hooks/useTranslate';
import { getAccountInitials, type SwitchableAccount } from 'utils/selectedAccount';

const avatarSxForKind = (kind: SwitchableAccount['kind']) =>
    kind === 'spouse'
        ? { bgcolor: 'info.main', color: 'common.white' }
        : { bgcolor: 'primary.main', color: 'primary.contrastText' };

const AccountSwitchToggle = () => {
    const { translate } = useTranslate();
    const { accounts, canSwitch, selected, switchTo } = useAccountSwitcher();
    const { data: profilePictureUrl } = useProfilePicture();
    const [pendingId, setPendingId] = useState<number | null>(null);

    if (!canSwitch || !selected || accounts.length < 2) {
        return null;
    }

    const switching = pendingId !== null;
    const selectedIndex = Math.max(
        0,
        accounts.findIndex((account) => account.id === selected.id)
    );

    const handleSelect = (account: SwitchableAccount) => {
        if (switching || account.id === selected.id) {
            return;
        }
        setPendingId(account.id);
        switchTo(account.id);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
                p: 0.5,
                borderRadius: 2.5,
                bgcolor: 'action.hover',
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    top: 4,
                    bottom: 4,
                    width: 'calc(50% - 7px)',
                    left: selectedIndex === 0 ? 4 : 'calc(50% + 3px)',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    transition: 'left 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
            />

            {accounts.map((account) => {
                const isSelected = account.id === selected.id;
                const isPending = pendingId === account.id;
                const fullName = getSwitchableAccountName(account);
                const label =
                    account.kind === 'self'
                        ? translate('accountMe')
                        : account.firstname?.trim() || translate('spouseAccount');
                const photo = account.kind === 'self' ? profilePictureUrl : undefined;

                return (
                    <Box
                        key={account.id}
                        component="button"
                        type="button"
                        onClick={() => handleSelect(account)}
                        disabled={switching}
                        aria-pressed={isSelected}
                        aria-label={`${translate('switchAccount')}: ${fullName || label}`}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            m: 0,
                            px: 0.5,
                            py: 0.85,
                            border: 'none',
                            borderRadius: 2,
                            bgcolor: 'transparent',
                            color: 'text.primary',
                            cursor: switching ? 'default' : 'pointer',
                            transition: 'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
                            transform: isSelected ? 'translateY(0)' : 'translateY(1px)',
                            '&:hover': {
                                transform: switching ? undefined : 'translateY(-1px)',
                            },
                            '&:disabled': { cursor: 'default' },
                            '&:focus-visible': {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={photo || undefined}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    ...avatarSxForKind(account.kind),
                                    boxShadow: (theme) =>
                                        isSelected
                                            ? `0 0 0 2px ${theme.palette.primary.main}`
                                            : '0 0 0 2px transparent',
                                    transform: isSelected ? 'scale(1)' : 'scale(0.9)',
                                    opacity: isSelected ? 1 : 0.72,
                                    transition:
                                        'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease, box-shadow 0.22s ease',
                                }}
                            >
                                {!photo && getAccountInitials(fullName || label)}
                            </Avatar>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: -2,
                                    bottom: -2,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    border: '2px solid',
                                    borderColor: 'background.paper',
                                    transform: isSelected || isPending ? 'scale(1)' : 'scale(0)',
                                    opacity: isSelected || isPending ? 1 : 0,
                                    transition:
                                        'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease',
                                }}
                            >
                                {isPending ? (
                                    <CircularProgress size={8} sx={{ color: 'inherit' }} />
                                ) : (
                                    <CheckRounded sx={{ fontSize: 10 }} />
                                )}
                            </Box>
                        </Box>
                        <Typography
                            noWrap
                            sx={{
                                maxWidth: '100%',
                                fontSize: 12,
                                fontWeight: isSelected ? 700 : 500,
                                lineHeight: 1.2,
                                color: isSelected ? 'primary.main' : 'text.secondary',
                                transition: 'color 0.2s ease',
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

export default AccountSwitchToggle;
